// AdminSchedulerLayout — správa naplánovaných úloh (jen pro is_admin = 'Y'), vzor AdminUsersLayout.jsx
// Statistiky + tabulka úloh + rozbalovací historie běhů + create/edit modal + manuální spuštění s pollingem.
import { useState, useEffect, Fragment } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { schedulerApi } from '../api/schedulerApi'
import { useAuthStore } from '../store/useAuthStore'
import { ModalOverlay } from '../components/ModalOverlay'
import { useTrackView } from '../hooks/useTrackProduct'

// ─── Pomocné komponenty a funkce ────────────────────────────────────────────────

function Badge({ value, yLabel = 'Ano', nLabel = 'Ne', yClass = 'bg-green-100 text-green-700', nClass = 'bg-gray-100 text-gray-500' }) {
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${value === 'Y' ? yClass : nClass}`}>
      {value === 'Y' ? yLabel : nLabel}
    </span>
  )
}

const STATUS_STYLE = {
  running: { label: 'Běží',      class: 'bg-blue-100 text-blue-700 animate-pulse' },
  success: { label: 'OK',        class: 'bg-green-100 text-green-700' },
  error:   { label: 'Chyba',     class: 'bg-red-100 text-red-700' },
  timeout: { label: 'Timeout',   class: 'bg-orange-100 text-orange-700' },
  killed:  { label: 'Zastaveno', class: 'bg-gray-200 text-gray-600' },
}

function StatusBadge({ status }) {
  if (!status) return <span className="text-xs text-gray-300">—</span>
  const s = STATUS_STYLE[status] || { label: status, class: 'bg-gray-100 text-gray-500' }
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${s.class}`}>{s.label}</span>
}

function formatDate(val) {
  if (!val) return '—'
  return new Date(val).toLocaleString('cs-CZ', { dateStyle: 'short', timeStyle: 'short' })
}

function formatDuration(ms) {
  if (ms === null || ms === undefined) return '—'
  if (ms < 1000) return `${ms}ms`
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`
  const m = Math.floor(ms / 60000)
  const s = Math.round((ms % 60000) / 1000)
  return `${m}m ${s}s`
}

function formatTimeout(sec) {
  if (sec === null || sec === undefined) return '—'
  return sec < 60 ? `${sec}s` : `${Math.round(sec / 60)} min`
}

function relativeTime(val) {
  if (!val) return '—'
  const diffMs = Date.now() - new Date(val).getTime()
  const diffMin = Math.round(diffMs / 60000)
  if (diffMin < 1) return 'právě teď'
  if (diffMin < 60) return `před ${diffMin} min`
  const diffH = Math.round(diffMin / 60)
  if (diffH < 24) return `před ${diffH} h`
  return formatDate(val)
}

// ─── Přehledové karty ────────────────────────────────────────────────────────

function StatsCards({ stats }) {
  if (!stats) return null
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
      <div className="flex flex-col gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Aktivní úlohy</p>
          <p className="text-2xl font-bold text-gray-800">{stats.enabled_jobs} <span className="text-sm font-normal text-gray-400">/ {stats.total_jobs}</span></p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Běhy za 24h</p>
          <div className="flex gap-3 text-sm">
            <span className="text-green-600 font-semibold">✓ {stats.success_24h}</span>
            <span className="text-red-600 font-semibold">✗ {stats.error_24h}</span>
            <span className="text-orange-500 font-semibold">⏱ {stats.timeout_24h}</span>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">Běhy za 7 dní</p>
          <div className="flex gap-3 text-sm">
            <span className="text-green-600 font-semibold">✓ {stats.success_7d}</span>
            <span className="text-red-600 font-semibold">✗ {stats.error_7d}</span>
          </div>
        </div>
      </div>
      <div className="md:col-span-2 bg-white rounded-xl border border-gray-200 p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Poslední chyby</p>
        {stats.recent_errors.length === 0 ? (
          <p className="text-xs text-gray-400">Žádné chyby</p>
        ) : (
          <div className="flex flex-col gap-2 max-h-64 overflow-y-auto">
            {stats.recent_errors.map(e => (
              <div key={e.id} className="text-xs border-b border-gray-100 last:border-0 pb-2 last:pb-0">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="font-medium text-gray-700">{e.job_name}</span>
                  <span className="text-gray-400 shrink-0">{formatDate(e.started_at)}</span>
                </div>
                {e.error_message && (
                  <p className="text-red-500 mt-0.5 line-clamp-2" title={e.error_message}>{e.error_message}</p>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

// ─── Log detail běhu (lazy load po kliknutí) ──────────────────────────────────

function RunLogDetail({ runId }) {
  const { data: run, isLoading } = useQuery({
    queryKey: ['scheduler-run-detail', runId],
    queryFn: () => schedulerApi.getRun(runId),
  })

  if (isLoading) return <p className="text-xs text-gray-400 px-3 py-2">Načítám log…</p>

  return (
    <div className="flex flex-col gap-2 px-3 py-2">
      {run.error_message && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{run.error_message}</p>
      )}
      <div>
        <p className="text-xs font-semibold text-gray-500 mb-1">stdout</p>
        <pre className="whitespace-pre-wrap text-xs bg-gray-900 text-gray-100 rounded-lg p-3 max-h-64 overflow-y-auto">{run.log_stdout || '(prázdné)'}</pre>
      </div>
      {run.log_stderr && (
        <div>
          <p className="text-xs font-semibold text-gray-500 mb-1">stderr</p>
          <pre className="whitespace-pre-wrap text-xs bg-gray-900 text-red-300 rounded-lg p-3 max-h-64 overflow-y-auto">{run.log_stderr}</pre>
        </div>
      )}
    </div>
  )
}

// ─── Rozbalovací sekce — historie běhů úlohy ──────────────────────────────────

function JobRunsRow({ jobId, onKill }) {
  const [expandedRunId, setExpandedRunId] = useState(null)

  const { data: runs = [], isLoading } = useQuery({
    queryKey: ['scheduler-job-runs', jobId],
    queryFn: () => schedulerApi.getJobRuns(jobId, { limit: 20 }),
  })

  return (
    <tr className="bg-blue-50/40">
      <td colSpan={9} className="px-6 py-3 border-b border-blue-100">
        <div className="flex flex-col gap-2">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Historie běhů</p>
          {isLoading && <p className="text-xs text-gray-400">Načítám…</p>}
          {!isLoading && runs.length === 0 && <p className="text-xs text-gray-400 italic">Žádné běhy</p>}
          {!isLoading && runs.length > 0 && (
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-400">
                  <th className="py-1 pr-3 font-medium">Spuštění</th>
                  <th className="py-1 pr-3 font-medium">Stav</th>
                  <th className="py-1 pr-3 font-medium">Začátek</th>
                  <th className="py-1 pr-3 font-medium">Trvání</th>
                  <th className="py-1 pr-3 font-medium">Exit</th>
                  <th className="py-1 pr-3 font-medium" />
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <Fragment key={r.id}>
                    <tr
                      className={`border-t border-blue-100/60 ${r.status !== 'running' ? 'cursor-pointer hover:bg-blue-100/30' : ''}`}
                      onClick={() => r.status !== 'running' && setExpandedRunId(prev => prev === r.id ? null : r.id)}
                    >
                      <td className="py-1.5 pr-3">
                        <span className="inline-block px-1.5 py-0.5 rounded text-xs bg-gray-100 text-gray-500">
                          {r.trigger === 'manual' ? 'ruční' : 'cron'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-3"><StatusBadge status={r.status} /></td>
                      <td className="py-1.5 pr-3 text-gray-500">{formatDate(r.started_at)}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{formatDuration(r.duration_ms)}</td>
                      <td className="py-1.5 pr-3 text-gray-500">{r.exit_code ?? '—'}</td>
                      <td className="py-1.5 pr-3 text-right">
                        {r.status === 'running' ? (
                          <button
                            onClick={(e) => { e.stopPropagation(); onKill(r.id) }}
                            className="px-2 py-0.5 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                          >
                            Zastavit
                          </button>
                        ) : (
                          <span className="text-gray-400">{expandedRunId === r.id ? '▼' : '▶'} Detail</span>
                        )}
                      </td>
                    </tr>
                    {expandedRunId === r.id && (
                      <tr>
                        <td colSpan={6}><RunLogDetail runId={r.id} /></td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </td>
    </tr>
  )
}

// ─── Modální dialog úlohy (create/edit) ───────────────────────────────────────

function buildPythonCommand(handler, args) {
  if (!handler) return ''
  const parts = ['python', `python/${handler}`, ...args.filter(a => a !== '')]
  return parts.map(p => (/\s/.test(p) ? `"${p}"` : p)).join(' ')
}

// Rozparsuje vložený python příkaz (s/bez "python", s/bez "python/" prefixu) na { handler, args }
function parsePythonCommand(raw, pythonScripts) {
  const tokens = raw.match(/"[^"]*"|'[^']*'|\S+/g) || []
  const clean = tokens.map(t => t.replace(/^["']|["']$/g, ''))
  if (clean.length === 0) return null

  let i = 0
  if (/^python3?$/i.test(clean[i])) i++

  const scriptRaw = clean[i]
  if (!scriptRaw) return null
  i++

  const scriptNormalized = scriptRaw.replace(/^python\//, '')
  const handler = pythonScripts.find(p => p === scriptNormalized || p === scriptRaw)
    ?? pythonScripts.find(p => p.endsWith(`/${scriptNormalized}`))
    ?? scriptNormalized

  return { handler, args: clean.slice(i) }
}

// ─── Cron builder — jednoduchý editor cron výrazu ─────────────────────────────

const DOW_LABELS = ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So']

function pad2(n) { return String(n).padStart(2, '0') }

// Zkusí rozpoznat cron výraz jako jeden z podporovaných jednoduchých vzorů, jinak null (= raw režim)
function parseCronSimple(expr) {
  const parts = (expr || '').trim().split(/\s+/)
  if (parts.length !== 5) return null
  const [min, hour, dom, month, dow] = parts

  if (month !== '*') return null

  // Každou minutu
  if (min === '*' && hour === '*' && dom === '*' && dow === '*') {
    return { freq: 'minute' }
  }

  // Každých N minut (*/N)
  if (/^\*\/\d+$/.test(min) && hour === '*' && dom === '*' && dow === '*') {
    return { freq: 'every_n_minutes', stepMinutes: Number(min.slice(2)) }
  }

  // Každých N hodin (*/N), v minutu X
  if (/^\d+$/.test(min) && /^\*\/\d+$/.test(hour) && dom === '*' && dow === '*') {
    return { freq: 'every_n_hours', stepHours: Number(hour.slice(2)), minute: Number(min) }
  }

  // Každou hodinu, v minutu X
  if (/^\d+$/.test(min) && hour === '*' && dom === '*' && dow === '*') {
    return { freq: 'hourly', minute: Number(min) }
  }

  if (!/^\d+$/.test(min) || !/^\d+$/.test(hour)) return null
  const minute = Number(min), h = Number(hour)

  // Denně
  if (dom === '*' && dow === '*') {
    return { freq: 'daily', hour: h, minute }
  }

  // Týdně (seznam dnů, čísla 0-6)
  if (dom === '*' && /^[0-6](,[0-6])*$/.test(dow)) {
    const days = dow.split(',').map(Number)
    return { freq: 'weekly', hour: h, minute, days }
  }

  // Měsíčně (den v měsíci)
  if (dow === '*' && /^\d+$/.test(dom)) {
    return { freq: 'monthly', hour: h, minute, day: Number(dom) }
  }

  return null
}

function buildCronSimple(s) {
  switch (s.freq) {
    case 'minute':          return '* * * * *'
    case 'every_n_minutes': return `*/${s.stepMinutes} * * * *`
    case 'every_n_hours':   return `${s.minute} */${s.stepHours} * * *`
    case 'hourly':          return `${s.minute} * * * *`
    case 'daily':           return `${s.minute} ${s.hour} * * *`
    case 'weekly':          return `${s.minute} ${s.hour} * * ${[...s.days].sort().join(',')}`
    case 'monthly':         return `${s.minute} ${s.hour} ${s.day} * *`
    default: return ''
  }
}

function describeCronSimple(s) {
  const time = s.hour !== undefined ? `v ${pad2(s.hour)}:${pad2(s.minute)}` : ''
  switch (s.freq) {
    case 'minute':          return 'Každou minutu'
    case 'every_n_minutes': return `Každých ${s.stepMinutes} min`
    case 'every_n_hours':   return `Každých ${s.stepHours} h, v minutu ${s.minute}`
    case 'hourly':          return `Každou hodinu v minutu ${s.minute}`
    case 'daily':           return `Každý den ${time}`
    case 'weekly': {
      if (!s.days || s.days.length === 0) return 'Vyberte alespoň jeden den'
      const names = [...s.days].sort().map(d => DOW_LABELS[d]).join(', ')
      return `Každý týden (${names}) ${time}`
    }
    case 'monthly': return `Každý měsíc ${s.day}. den ${time}`
    default: return ''
  }
}

const DEFAULT_SIMPLE = { freq: 'daily', hour: 3, minute: 0, days: [1], day: 1, stepMinutes: 5, stepHours: 2 }

function CronBuilder({ value, onChange }) {
  const initialParsed = parseCronSimple(value)
  const [mode, setMode] = useState(() => initialParsed ? 'simple' : 'raw')
  const [simple, setSimple] = useState(() => ({ ...DEFAULT_SIMPLE, ...initialParsed }))

  function updateSimple(patch) {
    const next = { ...simple, ...patch }
    setSimple(next)
    onChange(buildCronSimple(next))
  }

  function switchMode(newMode) {
    if (newMode === 'simple') {
      const parsed = parseCronSimple(value)
      if (!parsed && value.trim() !== '') return // nerozpoznaný výraz — zůstat v raw, nepřepisovat
      const next = { ...DEFAULT_SIMPLE, ...parsed }
      setSimple(next)
      onChange(buildCronSimple(next))
    }
    setMode(newMode)
  }

  function toggleDay(d) {
    const days = simple.days.includes(d) ? simple.days.filter(x => x !== d) : [...simple.days, d]
    updateSimple({ days })
  }

  return (
    <div className="flex flex-col gap-1 border-t border-gray-100 pt-3">
      <div className="flex items-center justify-between">
        <label className="text-xs text-gray-500 font-medium">Cron výraz *</label>
        <div className="flex text-xs rounded-lg border border-gray-300 overflow-hidden">
          <button type="button" onClick={() => switchMode('simple')}
            className={`px-2 py-1 transition-colors ${mode === 'simple' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
            Jednoduše
          </button>
          <button type="button" onClick={() => switchMode('raw')}
            className={`px-2 py-1 transition-colors border-l border-gray-300 ${mode === 'raw' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:bg-gray-50'}`}>
            Cron výraz
          </button>
        </div>
      </div>

      {mode === 'raw' && (
        <>
          <input
            type="text" required value={value} placeholder="0 3 * * *"
            onChange={e => onChange(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <p className="text-xs text-gray-400">formát: minuta hodina den měsíc den-v-týdnu</p>
        </>
      )}

      {mode === 'simple' && (
        <div className="flex flex-col gap-2">
          <select
            value={simple.freq}
            onChange={e => updateSimple({ freq: e.target.value })}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            <option value="minute">Každou minutu</option>
            <option value="every_n_minutes">Každých N minut</option>
            <option value="hourly">Každou hodinu</option>
            <option value="every_n_hours">Každých N hodin</option>
            <option value="daily">Denně</option>
            <option value="weekly">Týdně</option>
            <option value="monthly">Měsíčně</option>
          </select>

          {simple.freq === 'every_n_minutes' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Interval (minuty)</label>
              <input
                type="number" min={1} max={59} value={simple.stepMinutes}
                onChange={e => updateSimple({ stepMinutes: Number(e.target.value) })}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          {simple.freq === 'every_n_hours' && (
            <div className="flex gap-2">
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500">Interval (hodiny)</label>
                <input
                  type="number" min={1} max={23} value={simple.stepHours}
                  onChange={e => updateSimple({ stepHours: Number(e.target.value) })}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
              <div className="flex flex-col gap-1 flex-1">
                <label className="text-xs text-gray-500">V minutu</label>
                <input
                  type="number" min={0} max={59} value={simple.minute}
                  onChange={e => updateSimple({ minute: Number(e.target.value) })}
                  className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                />
              </div>
            </div>
          )}

          {simple.freq === 'hourly' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">V minutu</label>
              <input
                type="number" min={0} max={59} value={simple.minute}
                onChange={e => updateSimple({ minute: Number(e.target.value) })}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          {(simple.freq === 'daily' || simple.freq === 'weekly' || simple.freq === 'monthly') && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Čas</label>
              <input
                type="time" value={`${pad2(simple.hour)}:${pad2(simple.minute)}`}
                onChange={e => {
                  const [h, m] = e.target.value.split(':').map(Number)
                  updateSimple({ hour: h, minute: m })
                }}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          {simple.freq === 'weekly' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Dny v týdnu</label>
              <div className="flex gap-1 flex-wrap">
                {DOW_LABELS.map((label, d) => (
                  <button
                    key={d} type="button" onClick={() => toggleDay(d)}
                    className={`w-9 h-8 rounded-lg text-xs font-medium border transition-colors ${
                      simple.days.includes(d)
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 text-gray-500 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {simple.freq === 'monthly' && (
            <div className="flex flex-col gap-1">
              <label className="text-xs text-gray-500">Den v měsíci</label>
              <input
                type="number" min={1} max={31} value={simple.day}
                onChange={e => updateSimple({ day: Number(e.target.value) })}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          )}

          <div className="flex flex-col gap-1 pt-1">
            <p className="text-xs text-gray-500">{describeCronSimple(simple)}</p>
            <code className="text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 font-mono">{value || '—'}</code>
          </div>
        </div>
      )}
    </div>
  )
}

function JobModal({ job, isCopy = false, jsHandlers, pythonScripts, onClose }) {
  // isNew = vytváří se nová úloha (buď prázdná, nebo kopie existující)
  const isNew = !job || isCopy
  const qc = useQueryClient()
  const [copied, setCopied] = useState(false)

  const [form, setForm] = useState({
    name:         job ? (isCopy ? `${job.name} (kopie)` : job.name) : '',
    description:  job?.description  ?? '',
    job_type:     job?.job_type     ?? 'js',
    handler:      job?.handler      ?? '',
    args:         job?.args         ?? (job?.job_type === 'python' ? [] : {}),
    cron_expr:    job?.cron_expr    ?? '',
    timeout_sec:  job?.timeout_sec  ?? 3600,
    // kopie se vždy vytvoří neaktivní
    is_enabled:   isCopy ? 'N' : (job?.is_enabled ?? 'Y'),
    keep_last_runs: job?.keep_last_runs ?? '',
  })
  const [error, setError] = useState(null)
  const [pasteCmd, setPasteCmd] = useState('')
  const [pasteError, setPasteError] = useState(null)

  const mutation = useMutation({
    mutationFn: (data) => isNew ? schedulerApi.createJob(data) : schedulerApi.updateJob(job.id, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scheduler-jobs'] }); onClose() },
    onError: (err) => setError(err.message),
  })

  function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    mutation.mutate({ ...form, keep_last_runs: form.keep_last_runs === '' ? null : Number(form.keep_last_runs) })
  }

  function handleJobTypeChange(newType) {
    setForm(f => ({ ...f, job_type: newType, handler: '', args: newType === 'python' ? [] : {} }))
  }

  const selectedJsHandler = jsHandlers.find(h => h.name === form.handler)

  function setArgValue(name, value) {
    setForm(f => ({ ...f, args: { ...f.args, [name]: value } }))
  }

  function setPythonArg(idx, value) {
    setForm(f => ({ ...f, args: f.args.map((a, i) => i === idx ? value : a) }))
  }
  function addPythonArg() {
    setForm(f => ({ ...f, args: [...f.args, ''] }))
  }
  function removePythonArg(idx) {
    setForm(f => ({ ...f, args: f.args.filter((_, i) => i !== idx) }))
  }

  async function copyPythonCommand() {
    const cmd = buildPythonCommand(form.handler, form.args)
    if (!cmd) return
    await navigator.clipboard.writeText(cmd)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  function applyPastedCommand() {
    setPasteError(null)
    const parsed = parsePythonCommand(pasteCmd, pythonScripts)
    if (!parsed) return
    if (!pythonScripts.includes(parsed.handler)) {
      setPasteError(`Skript "${parsed.handler}" nebyl nalezen v seznamu dostupných skriptů.`)
      return
    }
    setForm(f => ({ ...f, handler: parsed.handler, args: parsed.args }))
    setPasteCmd('')
  }

  return (
    <ModalOverlay onClose={onClose} className="z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh]">
        <div className="flex items-center justify-between gap-4 px-8 pt-8 pb-4">
          <h2 className="text-lg font-bold text-gray-800">
            {isCopy ? `Kopie úlohy: ${job.name}` : isNew ? 'Nová úloha' : `Editace: ${job.name}`}
          </h2>
          <div className="flex items-center gap-2 shrink-0">
            <span className="text-sm text-gray-700">Aktivní</span>
            <button
              type="button"
              onClick={() => setForm(f => ({ ...f, is_enabled: f.is_enabled === 'Y' ? 'N' : 'Y' }))}
              className={`relative w-11 h-6 rounded-full transition-colors ${form.is_enabled === 'Y' ? 'bg-blue-500' : 'bg-gray-300'}`}
            >
              <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.is_enabled === 'Y' ? 'translate-x-5' : ''}`} />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0 flex-1">
          <div className="flex flex-col gap-3 px-8 pb-4 overflow-y-auto flex-1">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Název *</label>
            <input
              type="text" required value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Popis</label>
            <input
              type="text" value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Typ úlohy</label>
            <select
              value={form.job_type}
              onChange={e => handleJobTypeChange(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            >
              <option value="js">JS</option>
              <option value="python">Python</option>
            </select>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Handler *</label>
            {form.job_type === 'js' ? (
              <select
                required value={form.handler}
                onChange={e => setForm(f => ({ ...f, handler: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— vyberte —</option>
                {jsHandlers.map(h => <option key={h.name} value={h.name}>{h.label}</option>)}
              </select>
            ) : (
              <select
                required value={form.handler}
                onChange={e => setForm(f => ({ ...f, handler: e.target.value }))}
                className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                <option value="">— vyberte —</option>
                {pythonScripts.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            )}
          </div>

          {/* Dynamické parametry */}
          {form.job_type === 'js' && selectedJsHandler && (
            <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
              <p className="text-xs text-gray-500 font-medium">Parametry</p>
              {selectedJsHandler.paramsSchema.map(p => {
                const val = form.args[p.name] ?? p.default ?? ''
                if (p.type === 'boolean') {
                  return (
                    <div key={p.name} className="flex items-center justify-between">
                      <span className="text-sm text-gray-700">{p.label}</span>
                      <button
                        type="button"
                        onClick={() => setArgValue(p.name, !val)}
                        className={`relative w-11 h-6 rounded-full transition-colors ${val ? 'bg-blue-500' : 'bg-gray-300'}`}
                      >
                        <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${val ? 'translate-x-5' : ''}`} />
                      </button>
                    </div>
                  )
                }
                if (p.type === 'enum') {
                  return (
                    <div key={p.name} className="flex flex-col gap-1">
                      <label className="text-xs text-gray-500">{p.label}{p.required ? ' *' : ''}</label>
                      <select
                        required={p.required} value={val}
                        onChange={e => setArgValue(p.name, e.target.value)}
                        className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                      >
                        <option value="">— vyberte —</option>
                        {p.values.map(v => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </div>
                  )
                }
                return (
                  <div key={p.name} className="flex flex-col gap-1">
                    <label className="text-xs text-gray-500">{p.label}{p.required ? ' *' : ''}</label>
                    <input
                      type={p.type === 'number' ? 'number' : 'text'}
                      required={p.required} value={val}
                      onChange={e => setArgValue(p.name, p.type === 'number' ? Number(e.target.value) : e.target.value)}
                      className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  </div>
                )
              })}
            </div>
          )}

          {form.job_type === 'python' && (
            <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
              <div className="flex flex-col gap-1">
                <label className="text-xs text-gray-500 font-medium">Vložit python příkaz</label>
                <div className="flex items-center gap-2">
                  <input
                    type="text" value={pasteCmd}
                    onChange={e => setPasteCmd(e.target.value)}
                    placeholder='python python/import_oracle/import_daily.py --obs-type 10M --group-id 536'
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button type="button" onClick={applyPastedCommand}
                    className="px-2 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors shrink-0">
                    Rozparsovat
                  </button>
                </div>
                {pasteError && <p className="text-xs text-red-500">{pasteError}</p>}
              </div>

              <p className="text-xs text-gray-500 font-medium pt-1">Argumenty</p>
              {form.args.map((a, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <input
                    type="text" value={a}
                    onChange={e => setPythonArg(idx, e.target.value)}
                    className="flex-1 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  />
                  <button type="button" onClick={() => removePythonArg(idx)}
                    className="text-gray-300 hover:text-red-500 transition-colors font-bold leading-none px-2">×</button>
                </div>
              ))}
              <button type="button" onClick={addPythonArg}
                className="self-start text-xs text-blue-600 hover:text-blue-700 transition-colors">+ Přidat argument</button>

              {form.handler && (
                <div className="flex flex-col gap-1 pt-1">
                  <label className="text-xs text-gray-500 font-medium">Příkaz pro konzoli</label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 text-xs bg-gray-50 border border-gray-200 rounded-lg px-3 py-1.5 font-mono overflow-x-auto whitespace-nowrap">
                      {buildPythonCommand(form.handler, form.args)}
                    </code>
                    <button type="button" onClick={copyPythonCommand}
                      className="px-2 py-1.5 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors shrink-0">
                      {copied ? 'Zkopírováno ✓' : 'Kopírovat'}
                    </button>
                  </div>
                  <p className="text-xs text-gray-400">spustit z kořene cw-server repa</p>
                </div>
              )}
            </div>
          )}

          <CronBuilder
            value={form.cron_expr}
            onChange={val => setForm(f => ({ ...f, cron_expr: val }))}
          />

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Max. doba běhu (sekundy) *</label>
            <input
              type="number" required value={form.timeout_sec}
              onChange={e => setForm(f => ({ ...f, timeout_sec: Number(e.target.value) }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-400">≈ {Math.round(form.timeout_sec / 60)} min</p>
          </div>

          <div className="flex flex-col gap-1">
            <label className="text-xs text-gray-500 font-medium">Uchovat posledních N běhů</label>
            <input
              type="number" min={1} value={form.keep_last_runs}
              placeholder="neomezeno (řídí se globální retencí ve dnech)"
              onChange={e => setForm(f => ({ ...f, keep_last_runs: e.target.value }))}
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            />
            <p className="text-xs text-gray-400">prázdné = maže se podle stáří (úloha "Úklid historie běhů scheduleru")</p>
          </div>

          {error && (
            <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</p>
          )}
          </div>

          {/* Patička — nescroluje s obsahem */}
          <div className="flex gap-3 justify-end px-8 py-4 border-t border-gray-100 bg-white rounded-b-2xl">
            <button type="button" onClick={onClose}
              className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
              Zrušit
            </button>
            <button type="submit" disabled={mutation.isPending}
              className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-50 transition-colors">
              {mutation.isPending ? 'Ukládám…' : (isNew ? 'Vytvořit' : 'Uložit')}
            </button>
          </div>
        </form>
      </div>
    </ModalOverlay>
  )
}

// ─── Hlavní layout ─────────────────────────────────────────────────────────────

export function AdminSchedulerLayout() {
  const navigate = useNavigate()
  // Sledování přístupů (etapa K2) — admin obrazovky jsou systémové části bez uzlu v registru.
  useTrackView('admin', 'admin-scheduler')
  const { user } = useAuthStore()
  const qc = useQueryClient()

  const [modal, setModal] = useState(null)               // null | 'new' | { job, copy? }
  const [confirmDel, setConfirmDel] = useState(null)      // null | job
  const [confirmKill, setConfirmKill] = useState(null)    // null | { runId, jobId }
  const [confirmClearLogs, setConfirmClearLogs] = useState(null) // null | job
  const [expandedId, setExpandedId] = useState(null)      // null | job.id
  const [pollingRunId, setPollingRunId] = useState(null)  // null | number

  if (user && user.is_admin !== 'Y') {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Přístup odepřen — potřebujete práva administrátora.
      </div>
    )
  }

  const { data: jobs = [], isLoading, isError } = useQuery({
    queryKey: ['scheduler-jobs'],
    queryFn: schedulerApi.getJobs,
    enabled: !!user,
  })

  const { data: stats } = useQuery({
    queryKey: ['scheduler-stats'],
    queryFn: schedulerApi.getStats,
    enabled: !!user,
    refetchInterval: 30_000,
  })

  const { data: jsHandlers = [] } = useQuery({
    queryKey: ['scheduler-js-handlers'],
    queryFn: schedulerApi.getJsHandlers,
    enabled: !!user,
    staleTime: 5 * 60_000,
  })

  const { data: pythonScripts = [] } = useQuery({
    queryKey: ['scheduler-python-scripts'],
    queryFn: schedulerApi.getPythonScripts,
    enabled: !!user,
    staleTime: 5 * 60_000,
  })

  const deleteMutation = useMutation({
    mutationFn: (id) => schedulerApi.deleteJob(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['scheduler-jobs'] }); setConfirmDel(null) },
  })

  const clearLogsMutation = useMutation({
    mutationFn: (id) => schedulerApi.deleteJobRuns(id),
    onSuccess: (_res, jobId) => {
      qc.invalidateQueries({ queryKey: ['scheduler-jobs'] })
      qc.invalidateQueries({ queryKey: ['scheduler-job-runs', jobId] })
      qc.invalidateQueries({ queryKey: ['scheduler-stats'] })
      setConfirmClearLogs(null)
    },
  })

  const runMutation = useMutation({
    mutationFn: (id) => schedulerApi.runJob(id),
    onSuccess: (res, jobId) => {
      setPollingRunId(res.run_id)
      setExpandedId(jobId)
    },
  })

  const enableMutation = useMutation({
    mutationFn: (id) => schedulerApi.enableJob(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduler-jobs'] }),
  })

  const disableMutation = useMutation({
    mutationFn: (id) => schedulerApi.disableJob(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['scheduler-jobs'] }),
  })

  const killMutation = useMutation({
    mutationFn: (runId) => schedulerApi.killRun(runId),
    onSuccess: (_res, runId) => { setPollingRunId(runId); setConfirmKill(null) },
  })

  // Polling běžícího běhu, dokud nedosáhne terminálního stavu
  const { data: polledRun } = useQuery({
    queryKey: ['scheduler-run-poll', pollingRunId],
    queryFn: () => schedulerApi.getRun(pollingRunId),
    enabled: !!pollingRunId,
    refetchInterval: (query) => {
      const status = query.state.data?.status
      return (status === 'running' || !status) ? 2000 : false
    },
  })

  useEffect(() => {
    if (polledRun && polledRun.status !== 'running') {
      qc.invalidateQueries({ queryKey: ['scheduler-jobs'] })
      qc.invalidateQueries({ queryKey: ['scheduler-job-runs', polledRun.job_id] })
      setPollingRunId(null)
    }
  }, [polledRun, qc])

  function toggleExpand(id) {
    setExpandedId(prev => prev === id ? null : id)
  }

  function handleKill(runId, jobId) {
    setConfirmKill({ runId, jobId })
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hlavička */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center gap-4">
        <button onClick={() => navigate('/')}
          className="text-gray-400 hover:text-gray-700 transition-colors text-lg">
          ←
        </button>
        <h1 className="text-xl font-bold text-gray-800">Naplánované úlohy</h1>
        <span className="ml-auto text-sm text-gray-400">{jobs.length} úloh</span>
        <button
          onClick={() => setModal('new')}
          className="px-4 py-2 text-sm rounded-lg bg-blue-600 text-white font-semibold hover:bg-blue-700 transition-colors"
        >
          + Nová úloha
        </button>
      </div>

      {/* Obsah */}
      <div className="p-6">
        <StatsCards stats={stats} />

        {isLoading && <p className="text-gray-400 text-sm">Načítám…</p>}
        {isError && <p className="text-red-500 text-sm">Chyba při načítání úloh.</p>}

        {!isLoading && !isError && (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-8" />
                  {['Název', 'Typ', 'Cron', 'Max. doba', 'Uchovat běhů', 'Stav', 'Poslední běh', ''].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map(j => {
                  const isRunning = j.last_run_status === 'running'
                  return (
                    <Fragment key={j.id}>
                      <tr className={`border-b border-gray-100 hover:bg-gray-50 transition-colors ${isRunning ? 'bg-blue-50/40' : ''}`}>
                        <td className="pl-3">
                          <button
                            onClick={() => toggleExpand(j.id)}
                            className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-blue-600 hover:bg-blue-50 transition-colors text-xs"
                            title="Historie běhů"
                          >
                            {expandedId === j.id ? '▼' : '▶'}
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-gray-800" title={j.description || ''}>{j.name}</div>
                          {j.description && <div className="text-xs text-gray-400 truncate max-w-xs">{j.description}</div>}
                        </td>
                        <td className="px-4 py-3">
                          <span className="inline-block px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-700">
                            {j.job_type}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <code className="text-xs bg-gray-100 rounded px-1.5 py-0.5">{j.cron_expr}</code>
                        </td>
                        <td className="px-4 py-3 text-gray-500">{formatTimeout(j.timeout_sec)}</td>
                        <td className="px-4 py-3 text-gray-500">{j.keep_last_runs ?? '—'}</td>
                        <td className="px-4 py-3">
                          <button
                            type="button"
                            onClick={() => (j.is_enabled === 'Y' ? disableMutation : enableMutation).mutate(j.id)}
                            disabled={enableMutation.isPending || disableMutation.isPending}
                            title={j.is_enabled === 'Y' ? 'Vypnout' : 'Zapnout'}
                            className={`relative w-11 h-6 rounded-full transition-colors disabled:opacity-50 ${j.is_enabled === 'Y' ? 'bg-blue-500' : 'bg-gray-300'}`}
                          >
                            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${j.is_enabled === 'Y' ? 'translate-x-5' : ''}`} />
                          </button>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            <StatusBadge status={j.last_run_status} />
                            <span className="text-xs text-gray-400">{relativeTime(j.last_run_started_at)}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2 justify-end flex-wrap">
                            {isRunning ? (
                              <button
                                onClick={() => handleKill(j.last_run_id, j.id)}
                                className="px-3 py-1 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                              >
                                Zastavit
                              </button>
                            ) : (
                              <button
                                onClick={() => runMutation.mutate(j.id)}
                                disabled={runMutation.isPending}
                                className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
                              >
                                Spustit nyní
                              </button>
                            )}
                            <button onClick={() => setModal({ job: j })}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors">
                              Editovat
                            </button>
                            <button onClick={() => setModal({ job: j, copy: true })}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors">
                              Kopírovat
                            </button>
                            <button
                              onClick={() => setConfirmClearLogs(j)}
                              className="px-3 py-1 text-xs rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-100 transition-colors"
                            >
                              Smazat logy
                            </button>
                            <button
                              onClick={() => setConfirmDel(j)}
                              className="px-3 py-1 text-xs rounded-lg border border-red-200 text-red-500 hover:bg-red-50 transition-colors"
                            >
                              Smazat
                            </button>
                          </div>
                        </td>
                      </tr>

                      {expandedId === j.id && (
                        <JobRunsRow jobId={j.id} onKill={(runId) => handleKill(runId, j.id)} />
                      )}
                    </Fragment>
                  )
                })}
                {jobs.length === 0 && (
                  <tr><td colSpan={9} className="px-4 py-8 text-center text-gray-400">Žádné naplánované úlohy</td></tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal: nová / editace úlohy */}
      {modal !== null && (
        <JobModal
          job={modal === 'new' ? null : modal.job}
          isCopy={modal !== 'new' && !!modal.copy}
          jsHandlers={jsHandlers}
          pythonScripts={pythonScripts}
          onClose={() => setModal(null)}
        />
      )}

      {/* Potvrzení smazání */}
      {confirmDel && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
            <h3 className="text-lg font-bold text-gray-800">Smazat úlohu?</h3>
            <p className="text-sm text-gray-600">
              Opravdu chcete smazat úlohu <strong>{confirmDel.name}</strong>? Tato akce je nevratná.
            </p>
            {deleteMutation.isError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {deleteMutation.error?.message}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmDel(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Zrušit
              </button>
              <button
                onClick={() => deleteMutation.mutate(confirmDel.id)}
                disabled={deleteMutation.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {deleteMutation.isPending ? 'Mažu…' : 'Smazat'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Potvrzení smazání logů (historie běhů) */}
      {confirmClearLogs && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
            <h3 className="text-lg font-bold text-gray-800">Smazat logy úlohy?</h3>
            <p className="text-sm text-gray-600">
              Opravdu chcete smazat celou historii běhů úlohy <strong>{confirmClearLogs.name}</strong>? Tato akce je nevratná, běžící běh zůstane zachován.
            </p>
            {clearLogsMutation.isError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {clearLogsMutation.error?.message}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmClearLogs(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Zrušit
              </button>
              <button
                onClick={() => clearLogsMutation.mutate(confirmClearLogs.id)}
                disabled={clearLogsMutation.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {clearLogsMutation.isPending ? 'Mažu…' : 'Smazat logy'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Potvrzení zastavení běhu */}
      {confirmKill && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-xl p-6 w-full max-w-sm flex flex-col gap-4">
            <h3 className="text-lg font-bold text-gray-800">Zastavit běh?</h3>
            <p className="text-sm text-gray-600">
              Opravdu chcete zastavit běžící úlohu? Tato akce je nevratná.
            </p>
            {killMutation.isError && (
              <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                {killMutation.error?.message}
              </p>
            )}
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmKill(null)}
                className="px-4 py-2 text-sm rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors">
                Zrušit
              </button>
              <button
                onClick={() => killMutation.mutate(confirmKill.runId)}
                disabled={killMutation.isPending}
                className="px-4 py-2 text-sm rounded-lg bg-red-600 text-white font-semibold hover:bg-red-700 disabled:opacity-50 transition-colors"
              >
                {killMutation.isPending ? 'Zastavuji…' : 'Zastavit'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
